/**
 * Acting as / viewing as (ADR-006, operator decisions 14–18) — server state for the banner, the Team / People
 * entry points and Settings → Security → Acting-as log.
 *
 * Acting is a property of the session: after POST /sessions/act-as every request is the target's, so the
 * SPA re-reads /me (menu, capabilities, home route) and drops every other cached answer, which belonged to
 * the previous identity.
 */
import { useInfiniteQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ActAsLogQuery, MeResponse, Role, UserRef } from '@amplispot/contracts';
import { mayActAs } from '@amplispot/domain';
import { call, newIdempotencyKey } from '../api';
import { ME_KEY } from './session';

export { ACT_AS_MINUTES } from '@amplispot/domain';

export type ActMode = 'act' | 'view';

/** Everything cached except /me belongs to the identity that fetched it: clear it and refetch what is on screen. */
export function resetOtherIdentityData(qc: QueryClient): Promise<void> {
  return qc.resetQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
}

/** Re-read /me (the server is the source of truth for who this session is) and return it. */
export async function refreshMe(qc: QueryClient): Promise<MeResponse | undefined> {
  await qc.refetchQueries({ queryKey: ME_KEY, exact: true });
  return qc.getQueryData<MeResponse>(ME_KEY);
}

/**
 * Whether to OFFER "Act as" / "View as" for this person. Only the ladder (domain `mayActAs`, the same function
 * the API enforces): the founder → the AD, Ops, Sales, Marketing; the AD → Ops, Sales, Marketing. Never
 * yourself, never a disabled person, never while already acting (the capability is withdrawn then).
 */
export function canActAs(
  me: MeResponse,
  can: (c: 'actAs.member') => boolean,
  target: Pick<UserRef, 'id' | 'role'> & { status?: 'active' | 'disabled' },
): boolean {
  if (!can('actAs.member') || me.acting_as) return false;
  if (target.id === me.user.id || target.status !== 'active') return false;
  return mayActAs(me.user.role as Role, target.role);
}

/** POST /sessions/act-as → re-read /me (ActingBanner then drops the previous identity's data). */
export function useStartActAs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { user_id: string; reason: string; mode: ActMode; key: string }) =>
      call('actAs.start', { body: { user_id: v.user_id, reason: v.reason, mode: v.mode }, idempotencyKey: v.key }),
    // The new identity's /me re-renders the shell: screens that are not theirs unmount (Page redirects), then the
    // banner's identity effect clears everything else. Clearing here would refetch the old screen as them first.
    onSuccess: async () => {
      await qc.cancelQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
      await refreshMe(qc);
    },
  });
}

/** POST /sessions/act-as/stop (idempotent; allowed in view mode) → back to yourself. */
export function useStopActAs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => call('actAs.stop'),
    onSuccess: async () => {
      await qc.cancelQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
      await refreshMe(qc);
    },
  });
}

export type ActAsLogFilters = Pick<ActAsLogQuery, 'real_user_id' | 'target_id' | 'from' | 'to'>;

/** GET /security/act-as-log — newest first, 50 a page, following `next_cursor` on "Show more". */
export function useActAsLog(filters: ActAsLogFilters, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['actas-log', filters],
    queryFn: ({ pageParam }) => call('actAs.log', { query: { ...filters, limit: 50, cursor: pageParam } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });
}

export { newIdempotencyKey };
